import { Component, Input, Output, EventEmitter, HostListener } from '@angular/core';

export interface Column {
  name: string;
  label: string;
  width?: string;
  sortable?: boolean;
  headerColor?: string;
  textColor?: string;
  type?: 'text' | 'status' | 'price' | 'date';
  total?: string | number;
  // winepassport#385 — por defecto la celda de datos rompe en espacios (`break-words`, bien
  // para texto de varias palabras como Nombre/Dirección). Un token atómico sin espacios que no
  // entra (ids numéricos, códigos cortos) se parte letra por letra igual — poner `noWrap: true`
  // en esas columnas para que nunca wrapeen (si no entran, desbordan una sola línea).
  noWrap?: boolean;
}

interface Action {
  name: string;
  label: string;
  handler: (row: any) => void;
  condition?: (row: any) => boolean;
}

@Component({
  selector: 'app-metro-table',
  standalone: false,
  templateUrl: './metro-table.component.html',
})

export class MetroTableComponent {
  @Input() columns: Column[] = [];
  @Input() data: any[] = [];
  @Input() actions: Action[] = [];
  // winepassport#385 — cuando hay muchas acciones sueltas en la fila, agruparlas en un
  // menú de 3 puntos en vez de listarlas en línea. Opt-in: no cambia las tablas que no lo piden.
  @Input() actionsMenu: boolean = false;
  @Input() sortable: boolean = false;
  @Input() sortColumn: string = '';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';
  @Output() sortChange = new EventEmitter<{ column: string, direction: 'asc' | 'desc' }>();

  // Pagination
  @Input() pageSize: number = 10;
  @Input() currentPage: number = 1;
  @Input() isServerPaginated: boolean = false;
  @Input() totalItems: number = 0;
  @Output() pageChange = new EventEmitter<number>();

  @Input() footerTotal: number | null = null;


  // Row styling function
  @Input() getRowClass?: (row: any) => string;

  // winepassport#396 — los registros históricos sin fecha llegan como 0001-01-01 (el "mínimo" de .NET): se muestran como "-"
  // en vez de "01/01/0001". Aplica a toda columna de tipo 'date'.
  hasRealDate(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    const date = new Date(value as string | number | Date);
    return !isNaN(date.getTime()) && date.getUTCFullYear() > 1;
  }

  get paginatedData(): any[] {

    if (this.isServerPaginated) {
      return this.data;
    }

    if (!this.pageSize || this.pageSize <= 0) {
      return this.data;
    }
    const start = (this.currentPage - 1) * this.pageSize;
    return this.data.slice(start, start + this.pageSize);
  }

  getVisibleActionsForRow(row: any): Action[] {
    return this.actions.filter(action => !action.condition || action.condition(row));
  }

  // winepassport#385 — menú de 3 puntos: solo puede haber un menú abierto a la vez en la tabla.
  // No usa event.stopPropagation() (rompería el cierre de OTROS dropdowns de la misma página,
  // ej. el filtro app-metro-form-select de Sommeliers, que escucha document:click igual que acá)
  // — en cambio, igual que metro-form-select, se apoya en que el toggle corre antes que el
  // listener de document en el mismo click (bubbling normal) y se suprime esa única vez.
  openMenuRowId: any = null;
  private suppressNextClose = false;
  // winepassport#385 — posición del panel calculada desde el botón y renderizada con
  // `position: fixed` (no `absolute`) para que escape el contenedor `overflow-x-auto` de la
  // tabla: con solo overflow-x seteado, overflow-y computa a `auto`, así que un panel absoluto
  // quedaría recortado/con scroll propio en las últimas filas de la página en vez de mostrarse
  // completo por encima de la tabla.
  menuPosition = { top: 0, left: 0 };

  isMenuOpen(row: any, index: number): boolean {
    return this.openMenuRowId === (row.id ?? index);
  }

  toggleMenu(row: any, index: number, event: MouseEvent): void {
    const rowId = row.id ?? index;
    this.suppressNextClose = true;
    if (this.openMenuRowId === rowId) {
      this.openMenuRowId = null;
      return;
    }
    const btnRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.menuPosition = { top: btnRect.bottom + 4, left: Math.max(8, btnRect.right - 180) };
    this.openMenuRowId = rowId;
  }

  @HostListener('document:click')
  closeMenu(): void {
    if (this.suppressNextClose) {
      this.suppressNextClose = false;
      return;
    }
    this.openMenuRowId = null;
  }

  getActionLabel(action: any, row: any): string {
    if (typeof action.label === 'function') {
      return action.label(row);
    }
    return action.label;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.closeMenu();
    this.pageChange.emit(page);
  }

  get totalPages(): number {
    return Math.ceil(this.data.length / this.pageSize);
  }

  get localTotalItems(): number {
    return this.data.length;
  }

  onSort(columnName: string) {
    if (!this.sortable) return;

    this.closeMenu();
    let direction: 'asc' | 'desc' = 'asc';
    if (this.sortColumn === columnName) {
      direction = this.sortDirection === 'asc' ? 'desc' : 'asc';
    }

    this.sortColumn = columnName;
    this.sortDirection = direction;
    this.sortChange.emit({ column: columnName, direction });
  }

  get hasTotals(): boolean {
    return this.columns.some(col => col.total !== undefined && col.total !== null);
  }

  hasTotal(column: Column): boolean {
    return column.total !== undefined && column.total !== null;
  }

  getFormattedTotal(column: Column): string {
    if (!this.hasTotal(column)) return '';

    if (column.type === 'price') {
      const numValue = typeof column.total === 'number' ? column.total : parseFloat(column.total as string);
      // Usar el mismo formato que en las celdas de datos (number pipe con formato '1.0-0')
      // Formatear manualmente para que coincida exactamente
      const formatted = numValue.toLocaleString('es-ES', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0,
        useGrouping: true
      });
      return `$ ${formatted}`;
    }

    // Para columnas numéricas como stock, formatear como número
    if (column.name === 'stock' || typeof column.total === 'number') {
      const numValue = typeof column.total === 'number' ? column.total : parseFloat(column.total as string);
      if (!isNaN(numValue)) {
        return numValue.toLocaleString('en-US', { 
          minimumFractionDigits: 0, 
          maximumFractionDigits: 0,
          useGrouping: true
        });
      }
    }

    return column.total?.toString() || '';
  }
}
